import React, { useState } from 'react';
import '../styles/SurveyEditor.css';

const SurveyEditor = ({ survey, onSave, onClose }) => {
  const [editingSurvey, setEditingSurvey] = useState(survey || {
    name: '',
    description: '',
    category: 'Patient Satisfaction',
    estimatedTime: '5-10 minutes',
    questions: []
  });

  const [newQuestion, setNewQuestion] = useState({
    type: 'multiple-choice',
    question: '',
    options: [''],
    required: true
  });

  const questionTypes = [
    { value: 'multiple-choice', label: 'Multiple Choice' },
    { value: 'checkbox', label: 'Checkbox (Select Multiple)' },
    { value: 'text', label: 'Text Input' },
    { value: 'rating', label: 'Rating Scale (1-5)' },
    { value: 'yes-no', label: 'Yes/No' },
    { value: 'textarea', label: 'Long Text' }
  ];

  const surveyCategories = [
    'Patient Satisfaction',
    'Follow-up Care',
    'Medication Adherence',
    'Pain Assessment',
    'Mental Health Screening',
    'Quality of Life'
  ];

  const handleSurveyChange = (field, value) => {
    setEditingSurvey(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleQuestionChange = (field, value) => {
    setNewQuestion(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addOption = () => {
    setNewQuestion(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const updateOption = (index, value) => {
    setNewQuestion(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? value : opt)
    }));
  };

  const removeOption = (index) => {
    setNewQuestion(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const addQuestion = () => {
    // Ensure we have a valid question text
    const questionText = newQuestion.question.trim();
    if (!questionText) return;

    // Create the question object with proper text and options
    const question = {
      id: Date.now().toString(),
      type: newQuestion.type,
      question: questionText, // Use the trimmed question text
      required: newQuestion.required,
      // Only include options for question types that use them
      ...(newQuestion.type === 'multiple-choice' || newQuestion.type === 'checkbox' ? {
        options: newQuestion.options.filter(opt => opt.trim())
      } : {})
    };

    // Add the question to the survey
    setEditingSurvey(prev => ({
      ...prev,
      questions: [...prev.questions, question]
    }));

    // Reset the form with the same question type to maintain user flow
    setNewQuestion({
      type: newQuestion.type, // Keep the same question type
      question: '',
      options: [''],
      required: true
    });
  };

  const editQuestion = (questionId, updatedQuestion) => {
    setEditingSurvey(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId ? { ...q, ...updatedQuestion } : q
      )
    }));
  };

  const deleteQuestion = (questionId) => {
    setEditingSurvey(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId)
    }));
  };

  const moveQuestion = (questionId, direction) => {
    const questions = [...editingSurvey.questions];
    const index = questions.findIndex(q => q.id === questionId);
    
    if (direction === 'up' && index > 0) {
      [questions[index], questions[index - 1]] = [questions[index - 1], questions[index]];
    } else if (direction === 'down' && index < questions.length - 1) {
      [questions[index], questions[index + 1]] = [questions[index + 1], questions[index]];
    }

    setEditingSurvey(prev => ({ ...prev, questions }));
  };

  const handleSave = () => {
    if (!editingSurvey.name.trim()) {
      alert('Please enter a survey name');
      return;
    }
    if (editingSurvey.questions.length === 0) {
      alert('Please add at least one question');
      return;
    }

    const surveyToSave = {
      ...editingSurvey,
      id: survey?.id || Date.now().toString(),
      createdAt: survey?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    onSave(surveyToSave);
  };

  const renderQuestionPreview = (question, index) => (
    <div key={question.id} className="question-preview">
      <div className="question-header">
        <div className="question-info">
          <span className="question-number">Q{index + 1}</span>
          <span className="question-type">{questionTypes.find(t => t.value === question.type)?.label}</span>
          {question.required && <span className="required-badge">Required</span>}
        </div>
        <div className="question-actions">
          <button onClick={() => moveQuestion(question.id, 'up')} disabled={index === 0}>↑</button>
          <button onClick={() => moveQuestion(question.id, 'down')} disabled={index === editingSurvey.questions.length - 1}>↓</button>
          <button onClick={() => deleteQuestion(question.id)} className="delete-btn">×</button>
        </div>
      </div>
      <div className="question-content">
        <h4>{question.question}</h4>
        {(question.type === 'multiple-choice' || question.type === 'checkbox') && (
          <div className="options-preview">
            {question.options.map((option, i) => (
              <div key={i} className="option-item">
                <input 
                  type={question.type === 'checkbox' ? 'checkbox' : 'radio'} 
                  disabled 
                /> {option}
              </div>
            ))}
          </div>
        )}
        {question.type === 'rating' && (
          <div className="rating-preview">
            {[1, 2, 3, 4, 5].map(num => (
              <span key={num} className="rating-star">⭐</span>
            ))}
          </div>
        )}
        {question.type === 'yes-no' && (
          <div className="yes-no-preview">
            <input type="radio" disabled /> Yes
            <input type="radio" disabled /> No
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="survey-editor-overlay">
      <div className="survey-editor">
        <div className="editor-header">
          <h2>{survey ? 'Edit Survey' : 'Create New Survey'}</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <div className="editor-content">
          <div className="survey-details">
            <h3>Survey Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Survey Name *</label>
                <input
                  type="text"
                  value={editingSurvey.name}
                  onChange={(e) => handleSurveyChange('name', e.target.value)}
                  placeholder="Enter survey name"
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select
                  value={editingSurvey.category}
                  onChange={(e) => handleSurveyChange('category', e.target.value)}
                >
                  {surveyCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Estimated Time</label>
                <select
                  value={editingSurvey.estimatedTime}
                  onChange={(e) => handleSurveyChange('estimatedTime', e.target.value)}
                >
                  <option value="2-5 minutes">2-5 minutes</option>
                  <option value="5-10 minutes">5-10 minutes</option>
                  <option value="10-15 minutes">10-15 minutes</option>
                  <option value="15-20 minutes">15-20 minutes</option>
                  <option value="20+ minutes">20+ minutes</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={editingSurvey.description}
                onChange={(e) => handleSurveyChange('description', e.target.value)}
                placeholder="Brief description of the survey purpose"
                rows="3"
              />
            </div>
          </div>

          <div className="questions-section">
            <h3>Questions ({editingSurvey.questions.length})</h3>
            
            <div className="add-question">
              <h4>Add New Question</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Question Type</label>
                  <select
                    value={newQuestion.type}
                    onChange={(e) => handleQuestionChange('type', e.target.value)}
                  >
                    {questionTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={newQuestion.required}
                      onChange={(e) => handleQuestionChange('required', e.target.checked)}
                    />
                    Required
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label>Question Text *</label>
                <input
                  type="text"
                  value={newQuestion.question}
                  onChange={(e) => handleQuestionChange('question', e.target.value)}
                  placeholder="Enter your question"
                />
              </div>

              {(newQuestion.type === 'multiple-choice' || newQuestion.type === 'checkbox') && (
                <div className="options-editor">
                  <label>Answer Options</label>
                  {newQuestion.options.map((option, index) => (
                    <div key={index} className="option-input">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                        placeholder={`Option ${index + 1}`}
                      />
                      {newQuestion.options.length > 1 && (
                        <button onClick={() => removeOption(index)} className="remove-option">×</button>
                      )}
                    </div>
                  ))}
                  <button onClick={addOption} className="add-option-btn">+ Add Option</button>
                </div>
              )}

              <button onClick={addQuestion} className="add-question-btn">Add Question</button>
            </div>

            <div className="questions-list">
              {editingSurvey.questions.map((question, index) => 
                renderQuestionPreview(question, index)
              )}
              {editingSurvey.questions.length === 0 && (
                <div className="empty-questions">
                  <p>No questions added yet. Add your first question above.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="editor-footer">
          <button onClick={onClose} className="cancel-btn">Cancel</button>
          <button onClick={handleSave} className="save-btn">Save Survey</button>
        </div>
      </div>
    </div>
  );
};

export default SurveyEditor;
